import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Message } from '../../../../classes/Message';
import {
  CustomerService,
  OrganisationsService,
  TelephonyService,
  UserService,
} from '../../../../services';
import { ICustomerProfileDetail } from '../../../../interfaces/telephony/customer/customer-profile-detail.interface';
import { MessageService, SelectItem } from 'primeng';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ISupplierDetailedInfo } from '../../../../interfaces/telephony/supplier-detailed-info';
import { compare, Operation } from 'fast-json-patch';
import { IOrganisationPhoneNumber } from '../../../../interfaces/telephony/organisation-phone-number';
import { OrganisationTypeEnum } from '../../../../enums/organisation-types';

@Component({
  selector: 'app-customer-profile-detail',
  templateUrl: './customer-detail.component.html',
  styleUrls: ['../customer-information.component.scss'],
})
export class CustomerDetailComponent implements OnInit {
  constructor(
    private customerService: CustomerService,
    private messageService: MessageService,
    private fb: FormBuilder,
    public telephonyService: TelephonyService,
    private userService: UserService,
    private cdRef: ChangeDetectorRef,
  ) {}

  private _customerId: number;
  @Input()
  get customerId(): number {
    return this._customerId;
  }

  set customerId(value: number) {
    if (this._customerId !== value) {
      this._customerId = value;
      this.loadInformation();
    }
  }

  customerInfo: ICustomerProfileDetail;
  isBusy = false;
  editForm: FormGroup;
  showEditDetails = false;

  phoneNumberList: string;
  userOptions: SelectItem[];
  private toDelete: number[] = [];

  isCheckingNumber = false;
  originalNumbers: string[] = [];

  ngOnInit() {}

  loadInformation() {
    this.isBusy = true;

    this.customerService
      .getProfileDetail(this.customerId)
      .then((info) => {
        this.customerInfo = info;
        this.isBusy = false;

        void this.setPhoneNumberList();

        //console.log('Loaded customer info: ', info);
      })
      .catch((err) => {
        this.messageService.add(
          Message.Error('Load Customer Info', 'An error occurred loading customer information'),
        );
        this.isBusy = false;
      });

    this.userService
      .getUsersWithRoles({
        first: 0,
        rows: 99,
        filters: {
          organisationTypeId: {
            value: OrganisationTypeEnum.Engineius,
          },
          isActive: {
            value: 'true',
          },
        },
      })
      .then((res) => {
        res.items.sort((a, b) => {
          return (a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName);
        });
        this.userOptions = [
          { label: 'Select An Account Manager', value: null },
          ...res.items.map((x) => ({
            label: `${x.firstName} ${x.lastName}`,
            value: x.id,
          })),
        ];
      });
  }

  async setPhoneNumberList() {
    const info = this.customerInfo;

    let phoneNumbers: string[] = [];

    phoneNumbers = info.organisationPhoneNumbers.map((x) => x.phoneNumber);
    phoneNumbers.push(info.phoneNumber);

    // Create unique list of phone numbers
    phoneNumbers = phoneNumbers.filter((phoneNumber, index, self) => {
      return index === self.indexOf(phoneNumber);
    });

    this.phoneNumberList = phoneNumbers.join(' | ');
  }

  get f() {
    return this.editForm.value;
  }

  get phoneNumbers() {
    return this.editForm.controls['phoneNumbers'] as FormArray;
  }

  initEditAccountDetails() {
    this.initEditForm();
    this.setPhoneNumbers();

    this.showEditDetails = true;
  }

  private setPhoneNumbers() {
    this.phoneNumbers.clear();

    this.originalNumbers = [
      ...this.customerInfo.organisationPhoneNumbers.map((n) => {
        return n.phoneNumber;
      }),
    ];

    this.customerInfo.organisationPhoneNumbers.forEach((phone) => {
      this.addNewPhone(phone.id, phone.phoneNumber);
    });
  }

  initEditForm() {
    this.editForm = new FormGroup({
      id: new FormControl(this.customerId),
      description: new FormControl(this.customerInfo.description),
      accountManagerUserId: new FormControl(this.customerInfo.accountManagerUserId),
      phoneNumber: new FormControl(this.customerInfo.phoneNumber, Validators.required),
      phoneNumbers: new FormArray([]),
    });
  }

  addNewPhone(id = null, phoneNum: string = '') {
    // Don't allow us to create more than one blank line
    if (this.phoneNumbers.controls.some((control) => control?.value?.telephone?.trim() === '')) {
      return;
    }

    this.phoneNumbers.push(
      this.fb.group({
        id: new FormControl(id),
        telephone: new FormControl(phoneNum),
      }),
    );
  }

  deletePhone(index: number) {
    if (this.phoneNumbers.controls[index].value.id) {
      this.toDelete.push(this.phoneNumbers.controls[index].value.id);
    }

    this.phoneNumbers.removeAt(index);
  }

  async onPhoneNumberAdded(index, event: FocusEvent) {
    // ignore validation check if we're closing the dialog without saving
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget?.getAttribute('data-action') === 'cancel') {
      return;
    }

    const phoneNumber = this.phoneNumbers.at(index).get('telephone').value;

    console.log('Checking phone number: ', phoneNumber);

    // check and confirm if this will overwrite existing number
    this.isCheckingNumber = true;
    const canProceed = await this.telephonyService.checkExistingNumberAssignment(
      phoneNumber,
      null,
      this.customerId,
    );
    this.isCheckingNumber = false;

    if (!canProceed) {
      // remove the form control if it wasn't an update to an existing number, otherwise revert the number
      const isExistingNumber = index < this.originalNumbers.length;

      if (isExistingNumber) {
        // Revert to original number
        this.phoneNumbers.at(index).get('telephone').setValue(this.originalNumbers[index]);
      } else {
        // Remove only if it's a new number
        this.phoneNumbers.removeAt(index);
      }

      this.cdRef.detectChanges();
      return;
    }

    this.cdRef.detectChanges();
  }

  async saveEditForm() {
    if (this.isCheckingNumber) {
      return;
    }

    // Get the current form values
    const currentFormValue = this.editForm.value;

    // Create the main customer data object
    const customerData = {
      description: currentFormValue.description,
      accountManagerUserId: currentFormValue.accountManagerUserId,
      phoneNumber: currentFormValue.phoneNumber,
    };

    // Create the patch for the main customer data
    const customerPatch = compare(
      {
        description: this.customerInfo.description,
        accountManagerUserId: this.customerInfo.accountManagerUserId,
        phoneNumber: this.customerInfo.phoneNumber,
      },
      customerData,
    );

    // Handle phone numbers
    const phonePromises = [];

    // Handle existing phone numbers
    currentFormValue.phoneNumbers.forEach((phone) => {
      if (phone.id) {
        // Update existing phone number
        const originalPhone = this.customerInfo.organisationPhoneNumbers.find(
          (p) => p.id === phone.id,
        );
        if (originalPhone && originalPhone.phoneNumber !== phone.telephone) {
          phonePromises.push(
            this.telephonyService.updateOrganisationPhoneNumber(phone.id, phone.telephone),
          );
        }
      } else {
        // Add new phone number
        let dto = {
          organisationId: this.f.id,
          phoneNumber: phone.telephone,
        } as IOrganisationPhoneNumber;

        phonePromises.push(this.telephonyService.setOrganisationPhoneNumber(dto));
      }
    });

    // Handle deletions
    this.toDelete.forEach((phoneId) => {
      phonePromises.push(this.telephonyService.deleteTelephoneNumber(this.customerId, phoneId));
    });

    try {
      // Handle customer data update if needed
      if (customerPatch.length > 0) {
        await this.customerService.patch(this.customerId, customerPatch);
      }

      // Handle all phone number operations
      if (phonePromises.length > 0) {
        await Promise.all(phonePromises);
      }

      // If no changes were made
      if (customerPatch.length === 0 && phonePromises.length === 0) {
        this.showEditDetails = false;
        this.messageService.add(Message.Info('Customer Update', 'No changes were made'));
        return;
      }

      // Reload and update UI
      await this.loadInformation();
      this.showEditDetails = false;
      this.messageService.add(
        Message.Success('Customer Update', 'Customer details updated successfully'),
      );
      this.toDelete = []; // Reset the toDelete array
    } catch (error) {
      this.showEditDetails = false;
      this.messageService.add(
        Message.Error('Customer Update', 'An error occurred while updating customer details'),
      );
      console.error('Error updating customer:', error);
    }
  }
}
